import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import { useHideMessage } from "@/lib/api/messages.ts";

interface HideMessageDialogContentProps {
  onClose: () => void;
  messageId: string;
  isHidden: boolean;
  onSuccess: () => void;
}

/**
 * Inner content for the hide/unhide dialog, usable inside an externally
 * controlled Dialog root (e.g. when composed with DropdownMenu).
 */
export function HideMessageDialogContent({
  onClose,
  messageId,
  isHidden,
  onSuccess,
}: HideMessageDialogContentProps) {
  const { mutateAsync: hideMessage, isPending } = useHideMessage();

  const handleConfirm = async () => {
    try {
      await hideMessage({
        messageId,
        hidden: !isHidden,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      onClose();
    }
  };

  return (
    <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
      <div className="p-4">
        <DialogHeader>
          <DialogTitle className="mb-1 text-2xl font-semibold">
            {isHidden ? "Unhide message" : "Hide message"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {isHidden
              ? "This message will be visible to the mentor and teen again."
              : "This message will be hidden from the mentor and teen. You can unhide it at any time."}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex flex-row items-center gap-4">
          <Button
            className="w-full text-lg font-bold text-slate-900"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            No
          </Button>
          <Button
            className="w-full text-lg font-bold text-white"
            variant="default"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Yes
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

interface HideMessageDialogProps extends HideMessageDialogContentProps {
  open: boolean;
}

export function HideMessageDialog({
  open,
  onClose,
  ...contentProps
}: HideMessageDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <HideMessageDialogContent onClose={onClose} {...contentProps} />
    </Dialog>
  );
}
